import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import Dynamic from '@base/Dynamic/Dynamic';
import Dropdown from '@comp/Dropdown/Dropdown';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import pushRoute from '@utils/pushRoute';
import { Spacings, mediaQuery } from '@styles/index';
import {
  getPracticeAvailableLanguages,
  getPracticeTopologies
} from '@selectors/practiceSession';
import {
  fetchPracticeAvailableLanguages,
  fetchPracticeTopologies,
  setLanguagePreference
} from '@actions/practiceSession';
import {
  PRACTICE_LANGUAGE_CHOSEN,
  PRACTICE_PAGE_FILTERS_APPLIED
} from '@constants/segment';
import { getIsGTPGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import { emptyList } from '@constants/empty';
import usePracticeStore from '@stores/practice';
import usePracticeSegmentAnalytics from '../usePracticeSegmentAnalytics';
import { SUBJECTS, LANGUAGE } from '../constants';

const Subject = Dynamic(() => import('./Subject/Subject'));
const ConceptSelectorDialog = Dynamic(() =>
  import('./ConceptSelectorDialog/ConceptSelectorDialog')
);

const Container = styled.div`
  grid-column: 1 / 19;
`;

const DropdownLayout = styled.div`
  display: grid;
  grid-template-columns: 180px 180px;
  grid-gap: 16px;

  ${mediaQuery.uptoMobile} {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    margin-bottom: ${Spacings.SPACING_6B};
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const DropdownWrapper = styled.div`
  width: 100%;
`;

const StyledDropdown = styled(Dropdown)`
  width: 180px;
  margin-bottom: ${Spacings.SPACING_8B};

  ${mediaQuery.uptoMobile} {
    flex-shrink: 0;
    min-width: 180px;
  }
`;

const Loader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: span 18;
  margin: ${Spacings.SPACING_25B} 0px;
`;

const Content = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  const { goalSlug, goalUID, subject_uid, language } = router.query;
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const { practiceSelectedTopic: selectedTopic, setPracticeSelectedTopic } =
    usePracticeStore();

  const practiceTopologies = useSelectorWithShallowEqual(
    getPracticeTopologies(goalUID)
  );

  const availableLanguageInfo = useSelectorWithShallowEqual(
    getPracticeAvailableLanguages(goalUID)
  );

  const segmentAnalytics = usePracticeSegmentAnalytics();

  const availableSubjects = useMemo(() => {
    const data = [
      {
        name: 'All subjects',
        uid: 'All subjects',
        index: SUBJECTS.ALL
      }
    ];

    practiceTopologies.forEach(({ name, uid }, index) => {
      data.push({
        name,
        uid,
        index
      });
    });

    return data;
  }, [practiceTopologies]);

  const availableLanguages = useMemo(() => {
    return availableLanguageInfo.available_languages ?? emptyList;
  }, [availableLanguageInfo]);

  const [filterState, setFilterState] = useState({
    subject: availableSubjects[0],
    language: LANGUAGE.ALL
  });

  const updateFilter = (data) => {
    setFilterState((state) => ({
      ...state,
      ...data
    }));
  };

  useEffect(() => {
    // Always fetch on client side if data is not complete
    if (goalUID) {
      // Check if we have incomplete or missing language data
      const hasNoLanguageData = !availableLanguageInfo?.available_languages;
      const hasNoLanguageSelectionFlag =
        availableLanguageInfo?.language_selection_available === undefined;

      if (hasNoLanguageData || hasNoLanguageSelectionFlag) {
        dispatch(fetchPracticeAvailableLanguages(goalUID));
      }
    }
  }, [dispatch, goalUID, availableLanguageInfo]);

  useEffect(() => {
    if (subject_uid) {
      const subjectFromQuery = availableSubjects.find(
        ({ uid }) => uid === subject_uid
      );

      if (subjectFromQuery) {
        updateFilter({ subject: subjectFromQuery });
        return;
      }
    }

    updateFilter({ subject: availableSubjects[0] });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSubjects, subject_uid]);

  useEffect(() => {
    const languageInfo =
      availableLanguages?.find((lang) =>
        language
          ? lang.display_name === language
          : lang.code === availableLanguageInfo?.user_preference
      ) ?? LANGUAGE.ALL;

    updateFilter({ language: languageInfo });

    dispatch(
      fetchPracticeTopologies({
        uid: goalUID,
        languageCode: languageInfo.code,
        showAllChildren: isGTP
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLanguageInfo]);

  const updateQueryParams = ({
    isAllSubjectsSelected,
    isAllLanguagesSelected,
    subjectUID,
    languageName
  }) => {
    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/practice',
      query: {
        goalSlug,
        goalUID,
        ...(!isAllSubjectsSelected && {
          subject_uid: subjectUID
        }),
        ...(!isAllLanguagesSelected && {
          language: languageName
        })
      }
    });
  };

  const handleSelectedSubject = (subject) => {
    const isAllSubjectsSelected = subject.index === SUBJECTS.ALL;
    const isAllLanguagesSelected =
      filterState.language?.display_name &&
      filterState.language?.code === LANGUAGE.ALL.code;

    updateFilter({ subject });

    updateQueryParams({
      isAllSubjectsSelected,
      isAllLanguagesSelected,
      subjectUID: subject.uid,
      languageName: filterState.language.display_name
    });

    segmentAnalytics(PRACTICE_PAGE_FILTERS_APPLIED, {
      subject: isAllSubjectsSelected ? 'All subjects' : subject.name
    });
  };

  const handleSelectedLanguage = (languageObj) => {
    dispatch(
      setLanguagePreference({ goalUID, languageCode: languageObj.code })
    ).then((res) => {
      if (!res.error) {
        dispatch(
          fetchPracticeTopologies({
            uid: goalUID,
            languageCode: languageObj.code
          })
        );
      }
    });

    updateFilter({ language: languageObj });

    const isAllLanguagesSelected = languageObj.code === LANGUAGE.ALL.code;
    const isAllSubjectsSelected =
      filterState.subject?.uid && filterState.subject?.index === SUBJECTS.ALL;

    updateQueryParams({
      isAllSubjectsSelected,
      isAllLanguagesSelected,
      subjectUID: filterState.subject.uid,
      languageName: languageObj.display_name
    });

    segmentAnalytics(PRACTICE_LANGUAGE_CHOSEN, {
      language: isAllLanguagesSelected ? 'All' : languageObj.display_name
    });
  };

  const closeSelector = () => {
    setPracticeSelectedTopic({});
  };

  const isDrawerOpen = !!selectedTopic?.uid;
  const hasMoreThanOneSubject = availableSubjects.length > 2;
  const practiceTopicsLoaded = !!practiceTopologies?.length;
  const showAllSubjects = filterState.subject?.index === SUBJECTS.ALL;
  const showAllLanguages = filterState.language?.code === LANGUAGE.ALL.code;
  const showLanguageSelector =
    availableLanguageInfo.language_selection_available;

  const renderSubjectCards = () => {
    if (showAllSubjects) {
      return practiceTopologies.map((topology, index) => (
        <Subject key={topology.uid} topologyIndex={index} />
      ));
    }
    return (
      <Subject
        key={filterState.subject.uid}
        topologyIndex={filterState.subject.index}
      />
    );
  };

  if (practiceTopicsLoaded) {
    return (
      <>
        <DropdownLayout>
          <DropdownWrapper>
            {hasMoreThanOneSubject && (
              <StyledDropdown
                showSelectedBorder={!showAllSubjects}
                options={availableSubjects}
                selectedOption={filterState.subject}
                gridColumn="1/6"
                menuWidth="224px"
                stripLength={23}
                onOptionClick={handleSelectedSubject}
              />
            )}
          </DropdownWrapper>
          {showLanguageSelector && (
            <DropdownWrapper>
              <StyledDropdown
                showSelectedBorder={!showAllLanguages}
                options={availableLanguages}
                selectedOption={filterState.language}
                gridColumn="1/6"
                menuWidth="224px"
                stripLength={23}
                onOptionClick={handleSelectedLanguage}
              />
            </DropdownWrapper>
          )}
        </DropdownLayout>
        <Container>{renderSubjectCards()}</Container>
        <ConceptSelectorDialog
          drawerState={isDrawerOpen}
          onClose={closeSelector}
          isGTP={isGTP}
        />
      </>
    );
  }

  return (
    <Loader>
      <CircularLoader size={30} />
    </Loader>
  );
};

export default Content;
